import { Box, Typography, useTheme, alpha, Chip } from "@mui/material";
import { DepartmentEntity, PublicDocumentEntity, SeasonEntity, SegmentEntity } from "api/generated";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "components/CustomDialog";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { publicDocumentsApi } from "api";
import RecordAccordion from "./recordAccordion";
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderIcon from '@mui/icons-material/Folder';

interface AccordionData {
    departments: DepartmentEntity[],
    segments: SegmentEntity[],
    seasons: SeasonEntity[],
    documents: PublicDocumentEntity[],
    isManager: boolean,
    fetchDataCallBack: () => void,
    updateCallBack: (data: PublicDocumentEntity) => void
}


const PublicDocumentAccordion: FC<AccordionData> = ({ segments, departments, seasons, documents, isManager, fetchDataCallBack, updateCallBack }) => {

    const [departmentRecords, setDepartmentRecords] = useState<Record<number, PublicDocumentEntity[]>>([]);
    const [segmentRecords, setSegmentRecords] = useState<Record<number, PublicDocumentEntity[]>>([]);
    const [seasonRecords, setSeasonRecords] = useState<Record<number, PublicDocumentEntity[]>>([]);
    const [rest, setRest] = useState<PublicDocumentEntity[]>([]);
    const { t } = useTranslation();

    const [deletingDocumentId, setDeletingDocumentId] = useState<number>();
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

    const handleCloseDeleteConfirmation = () => {
        setDeletingDocumentId(undefined);
        setDeleteConfirmationOpen(false);
    }

    const handleConfirmDelete = async () => {
        if (deletingDocumentId)
            publicDocumentsApi.remove(deletingDocumentId).then(() => {
                handleCloseDeleteConfirmation();
                fetchDataCallBack();
            });
    }

    const handleOpenconfirmDelete = (id: number) => {
        setDeletingDocumentId(id);
        setDeleteConfirmationOpen(true);
    }

    const setRecords = () => {
        const departmentRecords: Record<number, PublicDocumentEntity[]> = [];
        const segmentRecords: Record<number, PublicDocumentEntity[]> = [];
        const seasonRecords: Record<number, PublicDocumentEntity[]> = [];
        const rest: PublicDocumentEntity[] = [];
        documents.forEach(document => {
            if (document.departments) {
                document.departments.forEach((department) => {
                    if (!departmentRecords[department.id]) {
                        departmentRecords[department.id] = [document];
                    }
                    else {
                        departmentRecords[department.id] = departmentRecords[department.id].concat(document);
                    }
                })
            }
            if (document.segments) {
                document.segments.forEach((segment) => {
                    if (!segmentRecords[segment.id]) {
                        segmentRecords[segment.id] = [document];
                    }
                    else {
                        segmentRecords[segment.id] = segmentRecords[segment.id].concat(document);
                    }
                })
            }
            if (document.seasons) {
                document.seasons.forEach((season) => {
                    if (!seasonRecords[season.id]) {
                        seasonRecords[season.id] = [document];
                    }
                    else {
                        seasonRecords[season.id] = seasonRecords[season.id].concat(document);
                    }
                })
            }
            if (document.departments?.length === 0 && document.seasons?.length === 0 && document.segments?.length === 0) {
                rest.push(document);

            }

        });
        setDepartmentRecords(departmentRecords);
        setSegmentRecords(segmentRecords);
        setSeasonRecords(seasonRecords);
        setRest(rest);
    }

    useEffect(() => {
        setRecords();
    }, [departments, seasons, segments, documents])


    const theme = useTheme();

    return (
        <>
            <CustomDialog
                open={deleteConfirmationOpen}
                onClose={handleCloseDeleteConfirmation}
                onConfirm={handleConfirmDelete}
                title={t('dialog.title', {
                    item: t('documents.itemName').toLocaleLowerCase(),
                })}
                content={t('dialog.document.delete')}
                icon={<DeleteForeverIcon />}
                confirmText={t('dialog.confirm')}
                confirmColor='error'
                cancelColor='primary'
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {Object.keys(departmentRecords).length > 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                            <Typography variant="h5" fontWeight="bold" color="text.primary">
                                {t('department.itemNamePlural') || 'Departments'}
                            </Typography>
                            <Chip 
                                label={Object.keys(departmentRecords).length} 
                                size="small" 
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                            />
                        </Box>
                        <RecordAccordion
                            sections={departments}
                            sectionRecords={departmentRecords}
                            handleOpenconfirmDeleteCallback={handleOpenconfirmDelete}
                            isManager={isManager}
                            updateCallBack={updateCallBack}
                            icon={<BusinessIcon />}
                        />
                    </Box>
                )}

                {Object.keys(segmentRecords).length > 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <CategoryIcon sx={{ color: theme.palette.secondary.main, fontSize: 24 }} />
                            <Typography variant="h5" fontWeight="bold" color="text.primary">
                                {t('segment.itemNamePlural') || 'Segments'}
                            </Typography>
                            <Chip 
                                label={Object.keys(segmentRecords).length} 
                                size="small" 
                                sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main }}
                            />
                        </Box>
                        <RecordAccordion
                            sections={segments}
                            sectionRecords={segmentRecords}
                            handleOpenconfirmDeleteCallback={handleOpenconfirmDelete}
                            isManager={isManager}
                            updateCallBack={updateCallBack}
                            icon={<CategoryIcon />}
                        />
                    </Box>
                )}

                {Object.keys(seasonRecords).length > 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <CalendarTodayIcon sx={{ color: theme.palette.info.main, fontSize: 24 }} />
                            <Typography variant="h5" fontWeight="bold" color="text.primary">
                                {t('season.itemNamePlural') || 'Seasons'}
                            </Typography>
                            <Chip 
                                label={Object.keys(seasonRecords).length} 
                                size="small" 
                                sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}
                            />
                        </Box>
                        <RecordAccordion
                            sections={seasons}
                            sectionRecords={seasonRecords}
                            handleOpenconfirmDeleteCallback={handleOpenconfirmDelete}
                            isManager={isManager}
                            updateCallBack={updateCallBack}
                            icon={<CalendarTodayIcon />}
                        />
                    </Box>
                )}

                {rest.length > 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <FolderIcon sx={{ color: theme.palette.warning.main, fontSize: 24 }} />
                            <Typography variant="h5" fontWeight="bold" color="text.primary">
                                {t('common.all')}
                            </Typography>
                            <Chip 
                                label={rest.length} 
                                size="small" 
                                sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}
                            />
                        </Box>
                        <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                            gap: 2
                        }}>
                            {rest.map((record) => {
                                const sections = [{ id: record.id, name: record.name }];
                                const sectionRecords = { [record.id]: [record] };
                                return (
                                    <RecordAccordion
                                        key={record.id}
                                        sections={sections}
                                        sectionRecords={sectionRecords}
                                        handleOpenconfirmDeleteCallback={handleOpenconfirmDelete}
                                        isManager={isManager}
                                        updateCallBack={updateCallBack}
                                        icon={<FolderIcon />}
                                        compact
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    )


}

export default PublicDocumentAccordion;